import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { numericAdaOrLeaveKeyFromClaim } from '../../../functions/todo-claim-numeric-key';
import { ListItem } from '../../../models/api/core/list/list-item';
import { SearchDiariesRequest } from '../../../models/api/todo/client-diaries/search-diaries-request';
import { bothOrNoneRequired } from '../../../validators/both-or-none-required';
import { BaseComponent } from '../../base/base.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { RangeComponent } from '../../core/controls/range.component';
import { SelectComponent } from '../../core/controls/select.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';
import { LoadingState } from '../../core/panels/loading-panel.component';

@Component({
  selector: 'app-diary-search',
  imports: [
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    SelectComponent,
    RangeComponent,
  ],
  template: `
    <form [formGroup]="fg">
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0 tw-mb-2 sm:tw-mb-0"
      >
        @if (isPageTodo) {
          <app-select
            [options]="clientOptions"
            optionLabel="description"
            optionValue="value"
            class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
            [control]="fg.controls.client"
            label="Client"
          />
        }
        <app-select
          [options]="diaryStatusOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.diaryStatus"
          label="Diary status"
          [multiple]="true"
        />
        <app-select
          [options]="diaryTypeOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.diaryType"
          label="Diary type"
          [multiple]="false"
          [disabled]="!isClientSelected"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0 tw-mb-2 sm:tw-mb-0"
      >
        <app-select
          [options]="diaryDueOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.diaryDue"
          label="Diary due"
          [multiple]="true"
        />
        <app-select
          [options]="userOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.followupUser"
          label="Follow-up user"
          [multiple]="false"
          [disabled]="!isClientSelected"
        />
        <app-select
          [options]="userOptions"
          optionLabel="description"
          optionValue="value"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [virtualScroll]="false"
          [control]="fg.controls.createdBy"
          label="Created by"
          [multiple]="false"
          [disabled]="!isClientSelected"
        />
      </div>
      <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-2 sm:tw-gap-0 tw-mb-2 sm:tw-mb-0"
      >
        <app-range
          label="Due date"
          [control]="fg.controls.dueDateStart"
          [toControl]="fg.controls.dueDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
        <app-range
          label="Create date"
          [control]="fg.controls.createDateStart"
          [toControl]="fg.controls.createDateEnd"
          class="tw-w-full sm:tw-basis-1/3 tw-min-w-0"
          [compact]="false"
        ></app-range>
      </div>
      @if (
        fg.invalid &&
        (fg.dirty || fg.touched) &&
        fg.errors?.['bothOrNoneRequired']
      ) {
        <div
          class="tw-text-[var(--sed-status-red-color)] tw-font-bold tw-text-[80%] tw-px-4 tw-pt-1 tw-pb-2 tw-leading-[14px] app-fade-in"
        >
          Error: {{ fg.errors['bothOrNoneRequired'] }}
        </div>
      }

      <div class="tw-pt-4"></div>
      <app-action-row useMobileLayout>
        <app-button
          right1
          type="submit"
          (click)="searchClick()"
          [loading]="loading"
          label="Search"
        />
        <app-button
          right2
          emphasis="Low"
          (click)="clearClick()"
          label="Clear"
        />
      </app-action-row>
    </form>
  `,
  styles: ``,
})
export class DiarySearchComponent extends BaseComponent implements OnInit {
  @Output()
  searchRequest = new EventEmitter<SearchDiariesRequest>();

  @Output()
  clear = new EventEmitter();

  @Input()
  loading = false;

  @Input()
  isPageTodo = false;

  isClientSelected = false;

  protected state: LoadingState = 'Loading';

  fg = new FormGroup(
    {
      filterDate: new FormControl<Date>(new Date()),
      client: new FormControl<ListItem[]>(null),
      diaryStatus: new FormControl<ListItem[]>(null),
      diaryDue: new FormControl<ListItem[]>(null),
      diaryType: new FormControl<ListItem[]>(null),
      dueDateStart: new FormControl<Date>(null),
      dueDateEnd: new FormControl<Date>(null),
      createDateStart: new FormControl<Date>(null),
      createDateEnd: new FormControl<Date>(null),
      followupUser: new FormControl<ListItem[]>(null),
      createdBy: new FormControl<ListItem[]>(null),
    },
    {
      validators: [
        bothOrNoneRequired(
          'dueDateStart',
          'dueDateEnd',
          'Please specify a valid due date range',
        ),
        bothOrNoneRequired(
          'createDateStart',
          'createDateEnd',
          'Please specify a valid create date range',
        ),
      ],
    },
  );

  diaryStatusOptions = [
    { description: 'Open', value: 'Open' },
    { description: 'Completed', value: 'Completed' },
  ];
  diaryDueOptions = [
    { description: 'Due today', value: 'Today' },
    { description: 'Past due', value: 'Past' },
    { description: 'Future due', value: 'Future' },
  ];
  diaryTypeOptions = [{ description: '---', value: 'x' }];

  userOptions = [{ description: '---', value: 'x' }];
  clientOptions = [{ description: '---', value: 'x' }];

  async ngOnInit() {
    this.state = 'Loaded';
    const list = await this.api.list.get();
    this.clientOptions = list.clients;

    if (!this.isPageTodo) {
      this.fg.controls.client.disable();
      await this.loadDiaryTypesForCurrentClient();
    }

    this.dispose.subs = this.fg.controls.client.valueChanges.subscribe(
      (value: any) => {
        this.onSelectClient(value);
      },
    );
  }

  async loadDiaryTypesForCurrentClient() {
    let clientNumber;
    const data = this.storage.get('current-diary');
    clientNumber = data?.clientNumber;

    if (!clientNumber) {
      const claim = this.view.currentClaim();
      clientNumber = claim.contractNumber;
    }

    if (clientNumber) {
      await this.onSelectClient(clientNumber);
    }
  }

  async onSelectClient(clientNumber: number) {
    this.fg.controls.diaryType.setValue(null);
    this.diaryTypeOptions = [{ description: '---', value: 'x' }];
    this.userOptions = [{ description: '---', value: 'x' }];
    this.fg.controls.diaryType.setValue(null);
    this.fg.controls.followupUser.setValue(null);
    this.fg.controls.createdBy.setValue(null);

    if (!clientNumber) {
      this.isClientSelected = false;
      return;
    }

    const data = this.storage.get('current-diary');
    const generalInfo = await this.api.clientDiary.getGeneralInfo({
      clientNumber: clientNumber,
      claimUID: data?.isClaimSpecific ? data.claimUID : null,
      absenceUID: data?.RecordSource === 'T' ? data.fileNumber : null,
      aDAEventNumber: data?.RecordSource === 'A' ? data.recordId : null,
      leaveNumber: data?.RecordSource === 'L' ? data.fileNumber : null,
    });
    this.diaryTypeOptions = generalInfo.diaryTypes.map((item: any) => ({
      description: item.description,
      value: item.typeUID,
    }));
    this.userOptions = generalInfo.users.map((item: any) => ({
      description: item.userName,
      value: item.userID,
    }));
    this.isClientSelected = true;
  }

  protected searchClick() {
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    } else {
      this.state = 'Loading';
      this.searchDiary();
    }
  }

  async clearClick() {
    this.fg.reset();

    if (!this.isPageTodo) {
      this.fg.controls.client.disable();
      await this.loadDiaryTypesForCurrentClient();
    }

    this.clear.emit();
  }

  async searchDiary() {
    let diaryDueSelection: string[] = [];
    if (this.fg.controls.diaryDue.value) {
      diaryDueSelection = this.fg.controls.diaryDue.value.map(
        (item: ListItem) => item.toString(),
      );
    }
    let diaryStatusSelection: string[] = [];
    if (this.fg.controls.diaryStatus.value) {
      diaryStatusSelection = this.fg.controls.diaryStatus.value.map(
        (item: ListItem) => item.toString(),
      );
    }
    const dueFuture = diaryDueSelection.includes('Future');
    const duePast = diaryDueSelection.includes('Past');
    const dueToday = diaryDueSelection.includes('Today');

    let diaryStatusValue;
    if (
      diaryStatusSelection.includes('Completed') &&
      diaryStatusSelection.includes('Open')
    ) {
      diaryStatusValue = null;
    } else if (diaryStatusSelection.includes('Open')) {
      diaryStatusValue = 1;
    } else if (diaryStatusSelection.includes('Completed')) {
      diaryStatusValue = 2;
    } else {
      diaryStatusValue = 0;
    }
    const request: SearchDiariesRequest = {
      clientNumber: this.fg.controls.client.value
        ? Number(this.fg.controls.client.value)
        : null,
      typeUID: this.fg.controls.diaryType.value
        ? Number(this.fg.controls.diaryType.value)
        : null,
      createdBy: this.fg.controls.createdBy.value,
      diaryStatus: diaryStatusValue,
      followUpBy: this.fg.controls.followupUser.value,
      createDateEnd: this.fg.controls.createDateEnd.value,
      createDateStart: this.fg.controls.createDateStart.value,
      dueDateEnd: this.fg.controls.dueDateEnd.value,
      dueDateStart: this.fg.controls.dueDateStart.value,
      dueFuture: dueFuture,
      duePast: duePast,
      dueToday: dueToday,
      isGadgetRequest: !diaryStatusSelection.includes('Draft'),
      ...(!this.isPageTodo && this.getClaimSpecificFields()),
    };

    this.searchRequest.emit(request);

    this.state = 'Loaded';
  }

  private getClaimSpecificFields(): any {
    const claim = this.view.currentClaim();
    if (!claim) {
      return {};
    }

    switch (claim.recordSource) {
      case 'J':
        return {
          claimUID: claim.recordId,
        };
      case 'T':
        return {
          absenceUID: claim.fileNumber,
          employeeUID: claim.hrMasterUid ? Number(claim.hrMasterUid) : null,
        };
      case 'A':
        return {
          aDAEventNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      case 'L':
        return {
          leaveNumber: numericAdaOrLeaveKeyFromClaim(claim),
        };
      default:
        return {};
    }
  }
}
